import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { teamApi } from '../api/teamApi';
import { TeamContext, type TeamContextValue } from './teamContextStore';
import { MA_ACTIVE_TEAM_ID_KEY } from '../constants/teamStorage';
import { showInfoToast } from '../utils/toast';

export const TeamProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, user, impersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ownedTeam, setOwnedTeam] =
    useState<TeamContextValue['ownedTeam']>(null);
  const [memberTeams, setMemberTeams] = useState<
    TeamContextValue['memberTeams']
  >([]);
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Guard hydration: avoid clearing localStorage before first `/team/my` finishes. */
  const [hydrated, setHydrated] = useState(false);
  /** True only when `/team/my` returned a valid payload in this session. */
  const [resolvedFromServer, setResolvedFromServer] = useState(false);
  const pendingRemovalNoticeRef = useRef<{
    teamId: string;
    label: string;
  } | null>(null);
  /** Identity key so impersonation start/stop reloads teams (auth stays true). */
  const identityKey =
    user?.id != null
      ? `${String(user.id)}:${impersonation?.impSessionId ?? 'self'}`
      : null;

  const clearTeamState = useCallback(() => {
    setOwnedTeam(null);
    setMemberTeams([]);
    setActiveTeamIdState(null);
    localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
    setHydrated(false);
    setResolvedFromServer(false);
  }, []);

  const allowedTeamIds = useMemo(() => {
    const s = new Set<string>();
    if (ownedTeam) s.add(String(ownedTeam.id));
    memberTeams.forEach((m) => s.add(String(m.teamId)));
    return s;
  }, [ownedTeam, memberTeams]);

  const userTeams = useMemo<TeamContextValue['userTeams']>(() => {
    const items: TeamContextValue['userTeams'] = [];
    if (ownedTeam) {
      items.push({
        id: String(ownedTeam.id),
        name: ownedTeam.name,
        ownerName: ownedTeam.ownerName,
        isOwner: true,
      });
    }
    memberTeams.forEach((t) => {
      items.push({
        id: String(t.teamId),
        name: t.name,
        ownerName: t.ownerName,
        isOwner: false,
      });
    });
    return items;
  }, [ownedTeam, memberTeams]);

  const currentTeam = useMemo<TeamContextValue['currentTeam']>(() => {
    if (!activeTeamId) return null;
    return (
      userTeams.find((team) => String(team.id) === String(activeTeamId)) || null
    );
  }, [activeTeamId, userTeams]);

  const setActiveTeamId = useCallback(
    (id: string | null) => {
      const normalized = id != null ? String(id) : null;
      if (normalized !== null && !allowedTeamIds.has(normalized)) {
        return;
      }
      setActiveTeamIdState(normalized);
      if (normalized) {
        localStorage.setItem(MA_ACTIVE_TEAM_ID_KEY, normalized);
      } else {
        localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
      }
    },
    [allowedTeamIds],
  );

  const refreshTeamContext = useCallback(async () => {
    if (!isAuthenticated || !identityKey) {
      clearTeamState();
      return;
    }
    setLoading(true);
    try {
      const res = await teamApi.getMyTeamContext();
      if (res.success && res.data) {
        setOwnedTeam(res.data.ownedTeam);
        setMemberTeams(res.data.memberTeams);
        setResolvedFromServer(true);
      } else {
        // Keep previous context on transient API/non-200 path.
        setResolvedFromServer(false);
      }
    } catch {
      // Keep previous context on transient network error.
      setResolvedFromServer(false);
    } finally {
      setLoading(false);
      setHydrated(true);
    }
  }, [isAuthenticated, identityKey, clearTeamState]);

  // Reload whenever the effective user changes (login, logout, impersonation).
  useEffect(() => {
    if (!isAuthenticated || !identityKey) {
      clearTeamState();
      return;
    }
    // Drop previous identity's teams immediately so UI never shows the wrong practice.
    setOwnedTeam(null);
    setMemberTeams([]);
    setActiveTeamIdState(null);
    localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
    setHydrated(false);
    setResolvedFromServer(false);
    void refreshTeamContext();
  }, [isAuthenticated, identityKey, refreshTeamContext, clearTeamState]);

  const reconcileActiveTeam = useCallback<
    TeamContextValue['reconcileActiveTeam']
  >(
    ({ redirectIfMissing = false } = {}) => {
      if (!isAuthenticated || !hydrated || loading || !resolvedFromServer)
        return;

      const isTeamWorkspaceRoute =
        location.pathname.startsWith('/team/workspace');
      const persistedId = localStorage.getItem(MA_ACTIVE_TEAM_ID_KEY);
      const candidateId =
        activeTeamId != null
          ? String(activeTeamId)
          : persistedId != null
            ? String(persistedId)
            : null;

      let nextActiveTeamId: string | null = null;
      if (candidateId && allowedTeamIds.has(candidateId)) {
        // Keep an explicit valid team selection.
        nextActiveTeamId = candidateId;
      } else if (candidateId) {
        // Stale team id (left/removed) — clear it.
        localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
        // Team workspace must have a team; doctor Personal scope may stay null.
        nextActiveTeamId = isTeamWorkspaceRoute
          ? ownedTeam?.id || memberTeams[0]?.teamId || null
          : null;
      } else {
        // Explicit Personal (null) or never set.
        // Only force a team on `/team/workspace/*` — elsewhere Personal is valid.
        nextActiveTeamId = isTeamWorkspaceRoute
          ? ownedTeam?.id || memberTeams[0]?.teamId || null
          : null;
      }

      if (nextActiveTeamId !== activeTeamId) {
        setActiveTeamIdState(nextActiveTeamId);
      }

      if (nextActiveTeamId) {
        localStorage.setItem(MA_ACTIVE_TEAM_ID_KEY, nextActiveTeamId);
      } else {
        localStorage.removeItem(MA_ACTIVE_TEAM_ID_KEY);
      }

      const pendingRemovalNotice = pendingRemovalNoticeRef.current;
      if (pendingRemovalNotice) {
        const removedFromSelectedTeam = !allowedTeamIds.has(
          pendingRemovalNotice.teamId,
        );
        if (removedFromSelectedTeam) {
          showInfoToast(`You were removed from ${pendingRemovalNotice.label}.`);
        }
        pendingRemovalNoticeRef.current = null;
      }

      if (redirectIfMissing && !nextActiveTeamId && isTeamWorkspaceRoute) {
        navigate('/team', { replace: true });
      }
    },
    [
      isAuthenticated,
      hydrated,
      loading,
      resolvedFromServer,
      activeTeamId,
      allowedTeamIds,
      ownedTeam,
      memberTeams,
      location.pathname,
      navigate,
    ],
  );

  useEffect(() => {
    reconcileActiveTeam();
  }, [reconcileActiveTeam]);

  useEffect(() => {
    const onTeamMembershipStale = () => {
      if (activeTeamId) {
        const active = userTeams.find((t) => t.id === activeTeamId);
        pendingRemovalNoticeRef.current = {
          teamId: activeTeamId,
          label: active?.name || 'selected team',
        };
      }
      void refreshTeamContext();
    };

    window.addEventListener(
      'meraayurved:team-membership-stale',
      onTeamMembershipStale,
    );
    return () => {
      window.removeEventListener(
        'meraayurved:team-membership-stale',
        onTeamMembershipStale,
      );
    };
  }, [activeTeamId, userTeams, refreshTeamContext]);

  const value: TeamContextValue = {
    ownedTeam,
    memberTeams,
    userTeams,
    currentTeam,
    activeTeamId,
    setActiveTeamId,
    loading,
    refreshTeamContext,
    reconcileActiveTeam,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};
